import type React from 'react';
import { useEffect, useState } from 'react';
import { api } from 'src/services/api';

interface BalanceResponse {
  currency: string;
  available_amount: number;
  waiting_funds_amount: number;
  transferred_amount: number;
  recipient: {
    id: string;
    name: string;
    email: string;
    document: string;
    type: string;
    status: string;
    created_at: string;
    updated_at: string;
  };
}

interface Props {
  sellerId?: string;
}

export const SellerBalanceSummary: React.FC<Props> = ({ sellerId }) => {
  const [balance, setBalance] = useState<BalanceResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!sellerId) {
      setLoading(false);
      return;
    }

    const fetchBalance = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.get(`/seller/balance/${sellerId}`);
        setBalance(response.data);
      } catch (err) {
        setError('Erro ao carregar saldo.');
      } finally {
        setLoading(false);
      }
    };

    fetchBalance();
  }, [sellerId]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: balance?.currency || 'BRL',
    }).format(value / 100);
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'bg-red-100 text-red-700';
      case 'pending':
        return 'bg-yellow-100 text-yellow-700';
      case 'inactive':
        return 'bg-gray-100 text-gray-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusText = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'Ativo';
      case 'pending':
        return 'Pendente';
      case 'inactive':
        return 'Inativo';
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="w-8 h-8 mx-auto border-4 border-rose-300 border-t-rose-600 rounded-full animate-spin"></div>
        <p className="mt-4 text-rose-700 font-medium">Carregando...</p>
      </div>
    );
  }

  if (error || !balance) {
    return (
      <div className="text-center py-8">
        <p className="text-rose-700 font-semibold">
          {error || 'Falha ao carregar dados.'}
        </p>
        <button
          onClick={() => window.location.reload()}
          className="mt-4 px-4 py-2 bg-rose-600 text-white rounded hover:bg-rose-700 transition"
        >
          Tentar novamente
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-2xl px-2 md:mx-auto md:px-4 py-6 text-gray-900">
      <h2 className="text-xl font-bold text-rose-700 mb-4">
        Resumo da Conta - Pagar.me
      </h2>

      <div className="space-y-3">
        <div>
          <p className="text-sm text-gray-600">Saldo Disponível</p>
          <p className="text-lg font-bold text-rose-800">
            {formatCurrency(balance.available_amount)}
          </p>
        </div>

        <div>
          <p className="text-sm text-gray-600">Saldo a Receber</p>
          <p className="text-lg font-bold text-rose-800">
            {formatCurrency(balance.waiting_funds_amount)}
          </p>
        </div>

        <div>
          <p className="text-sm text-gray-600">Total Transferido</p>
          <p className="text-lg font-bold text-rose-800">
            {formatCurrency(balance.transferred_amount)}
          </p>
        </div>

        <div className="border-t border-gray-200 pt-4">
          <p className="text-sm text-gray-600">
            Saldo Total (Disponível + A Receber)
          </p>
          <p className="text-2xl font-extrabold text-rose-900">
            {formatCurrency(
              balance.available_amount + balance.waiting_funds_amount
            )}
          </p>
        </div>

        <div className="border-t border-gray-200 pt-4 space-y-2">
          <p className="text-sm font-semibold text-gray-700">
            Informações do Recebedor:
          </p>

          <div>
            <span className="text-xs text-gray-500">Nome:</span>
            <p className="text-sm font-medium">{balance.recipient.name}</p>
          </div>

          <div>
            <span className="text-xs text-gray-500">Email:</span>
            <p className="text-sm font-medium break-all">
              {balance.recipient.email}
            </p>
          </div>

          <div>
            <span className="text-xs text-gray-500">Documento:</span>
            <p className="text-sm font-medium">{balance.recipient.document}</p>
          </div>

          <div>
            <span className="text-xs text-gray-500">Status:</span>
            <span
              className={`ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${getStatusColor(balance.recipient.status)}`}
            >
              {getStatusText(balance.recipient.status)}
            </span>
          </div>

          <div className="text-xs text-gray-500 flex justify-between pt-2">
            <span>
              Criado:{' '}
              {new Date(balance.recipient.created_at).toLocaleDateString(
                'pt-BR'
              )}
            </span>
            <span>
              Atualizado:{' '}
              {new Date(balance.recipient.updated_at).toLocaleDateString(
                'pt-BR'
              )}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
